/**
 * useInvoiceAgentBridge
 *
 * AI ajanının fatura formunu EKRANDA doldurmasını sağlayan köprü.
 * SalesInvoice / PurchaseInvoice mount olduğunda uiBridge'e imperatif bir
 * form API'si kaydeder; ajan executor'ları bu API üzerinden cari seçer,
 * kalem ekler/düzenler, alanları ayarlar ve (onaydan sonra) kaydeder.
 *
 * Tasarım notları:
 * - Sayfa state'ine ref üzerinden erişilir (cfgRef her render'da güncellenir)
 *   → stale closure yok, API bir kez register edilir.
 * - Ürün araması sayfanın kendi listesine değil doğrudan paged API'ye gider;
 *   sayfadaki liste sayfalı/filtreli olduğundan güvenilir eşleşme sağlanır.
 * - Tüm metodlar ajana functionResponse olarak dönecek JSON üretir
 *   (success/error + kısa Türkçe mesaj).
 */

import { useEffect, useRef } from 'react';
import {
  registerInvoiceFormApi,
  type InvoiceFormApi,
  type InvoiceFormFields,
  type InvoiceFormItemInput,
  type InvoiceFormItemTarget,
  type InvoiceFormType,
} from '../../../aichat/agent/uiBridge';
import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';
import { recalculateLineItem, formatQuantityForDisplay } from '../utils/invoiceCalculations';
import type { InvoiceItem } from '../components/types/invoice.types';

export interface InvoiceAgentBridgeState {
  accountName: string | null;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  items: InvoiceItem[];
  subtotal: number;
  vatTotal: number;
  grandTotal: number;
  error: string | null;
}

export interface InvoiceAgentBridgeConfig {
  formType: InvoiceFormType;
  /** false → köprü kaydedilmez (ör. teklif/sipariş modları) */
  enabled: boolean;
  /** Sayfanın kendi cari seçim handler'ı (modalı da kapatır) */
  selectAccount: (account: any) => void;
  /**
   * Form alanı ayarla; sayfa alanı desteklemiyorsa false döndürür.
   * (paymentMethod satışta var, alışta yok gibi farklar bu şekilde raporlanır.)
   */
  setField: (field: string, value: any) => boolean;
  /** Kalem listesi güncelleme (React setState updater biçiminde) */
  setItems: (updater: (prev: InvoiceItem[]) => InvoiceItem[]) => void;
  getDefaultWarehouseId: () => string | undefined;
  getState: () => InvoiceAgentBridgeState;
  save: () => Promise<void>;
}

const trLower = (s: any): string => String(s ?? '').toLocaleLowerCase('tr-TR');

/** Ad eşleştirme: önce tam, sonra baştan, sonra içerir. */
const matchRowByName = <T,>(rows: T[], nameOf: (r: T) => string, query: string): T | undefined => {
  const q = trLower(query);
  return (
    rows.find((r) => trLower(nameOf(r)) === q) ||
    rows.find((r) => trLower(nameOf(r)).startsWith(q)) ||
    rows.find((r) => trLower(nameOf(r)).includes(q))
  );
};

const findItemIndex = (items: InvoiceItem[], target: InvoiceFormItemTarget): number => {
  if (target.line !== undefined && target.line !== null) {
    const idx = Number(target.line) - 1;
    return Number.isInteger(idx) && idx >= 0 && idx < items.length ? idx : -1;
  }
  const q = trLower(target.itemName);
  if (!q) return -1;
  const exact = items.findIndex((i) => trLower(i.itemName) === q);
  if (exact >= 0) return exact;
  return items.findIndex((i) => trLower(i.itemName).includes(q));
};

/** Ajan girdisindeki değişiklikleri kaleme uygular ve satırı yeniden hesaplar. */
const applyItemChanges = (item: InvoiceItem, ch: InvoiceFormItemInput): InvoiceItem => {
  const next: InvoiceItem = { ...item };
  if (ch.quantity !== undefined) {
    const q = Number(ch.quantity);
    if (q > 0) {
      next.quantity = q;
      next.displayQuantity = formatQuantityForDisplay(q);
    }
  }
  if (ch.unitPrice !== undefined) next.enteredPrice = Number(ch.unitPrice) || 0;
  if (ch.priceType === 'VAT_EXCLUDED' || ch.priceType === 'VAT_INCLUDED') next.priceType = ch.priceType;
  if (ch.vatRate !== undefined) next.vatRate = Number(ch.vatRate) || 0;
  if (ch.discountRate !== undefined) {
    next.discountType = 'PERCENT';
    next.discountRate = Number(ch.discountRate) || 0;
  }
  if (ch.discountAmount !== undefined) {
    next.discountType = 'AMOUNT';
    next.discountAmount = Number(ch.discountAmount) || 0;
  }
  if (ch.unit) next.unit = String(ch.unit);
  if (ch.itemName) next.itemName = String(ch.itemName);
  if (ch.warehouseId) next.warehouseId = String(ch.warehouseId);
  return recalculateLineItem(next);
};

/** Ajana dönen kompakt kalem özeti (satır no 1 tabanlı). */
const summarizeItems = (items: InvoiceItem[]) =>
  items.map((i, idx) => ({
    line: idx + 1,
    itemName: i.itemName,
    quantity: i.quantity,
    unit: i.unit,
    unitPrice: i.enteredPrice,
    priceType: i.priceType,
    vatRate: i.vatRate,
    discountRate: i.discountRate,
    discountAmount: i.discountAmount,
    lineTotal: i.grossTotal,
    isManual: i.itemId === 'manual_item',
  }));

export const useInvoiceAgentBridge = (config: InvoiceAgentBridgeConfig): void => {
  const cfgRef = useRef(config);
  cfgRef.current = config;

  useEffect(() => {
    if (!config.enabled) return undefined;

    const api: InvoiceFormApi = {
      formType: config.formType,

      setAccountByName: async (name: string) => {
        const q = String(name ?? '').trim();
        if (!q) return { success: false, error: 'Cari adı boş olamaz.' };
        const resp = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
        const raw = resp?.data?.data ?? resp?.data ?? [];
        const rows: any[] = Array.isArray(raw) ? raw : [];
        const found = matchRowByName(rows, (r) => r.companyName ?? r.CompanyName ?? '', q);
        if (!found) {
          const firstWord = q.split(/\s+/)[0];
          const suggestions = rows
            .filter((r) => trLower(r.companyName ?? r.CompanyName).includes(trLower(firstWord)))
            .slice(0, 5)
            .map((r) => r.companyName ?? r.CompanyName);
          return {
            success: false,
            error: `Cari bulunamadı: "${q}"`,
            ...(suggestions.length > 0 ? { suggestions } : {}),
          };
        }
        cfgRef.current.selectAccount(found);
        return {
          success: true,
          message: `Cari seçildi: ${found.companyName ?? found.CompanyName}`,
          accountId: found.id ?? found.Id,
        };
      },

      setFields: (fields: InvoiceFormFields) => {
        const applied: string[] = [];
        const unsupported: string[] = [];
        Object.entries(fields || {}).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (cfgRef.current.setField(key, value)) applied.push(key);
          else unsupported.push(key);
        });
        if (applied.length === 0 && unsupported.length === 0) {
          return { success: false, error: 'Ayarlanacak alan verilmedi.' };
        }
        return {
          success: applied.length > 0,
          applied,
          ...(unsupported.length > 0
            ? { unsupported, note: 'Bu alanlar bu form tipinde desteklenmiyor.' }
            : {}),
        };
      },

      addItem: async (input: InvoiceFormItemInput) => {
        const name = String(input.itemName ?? '').trim();
        if (!name) return { success: false, error: 'itemName zorunlu.' };
        const qty = Number(input.quantity) > 0 ? Number(input.quantity) : 1;
        const defaultWhId = input.warehouseId || cfgRef.current.getDefaultWarehouseId();

        if (input.isManual === true) {
          const manual = recalculateLineItem({
            id: 'manual_' + Date.now().toString(),
            itemId: 'manual_item',
            itemName: name,
            quantity: qty,
            displayQuantity: formatQuantityForDisplay(qty),
            unit: input.unit || 'adet',
            enteredPrice: Number(input.unitPrice) || 0,
            priceType: input.priceType === 'VAT_INCLUDED' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED',
            unitPrice: 0,
            unitPriceWithVat: 0,
            vatRate: input.vatRate !== undefined ? Number(input.vatRate) || 0 : 20,
            sctRate: 0,
            discountRate: Number(input.discountRate) || 0,
            discountAmount: Number(input.discountAmount) || 0,
            discountType: input.discountAmount !== undefined ? 'AMOUNT' : 'PERCENT',
            warehouseId: defaultWhId,
            lineTotal: 0,
            netTotal: 0,
            vatAmount: 0,
            sctAmount: 0,
            grossTotal: 0,
          });
          cfgRef.current.setItems((prev) => [...prev, manual]);
          return {
            success: true,
            message: `Stoksuz kalem eklendi: ${name} x${qty}`,
            line: cfgRef.current.getState().items.length + 1,
          };
        }

        // Stoklu ürün: paged API'de ara (sayfadaki liste sayfalı olduğundan güvenilir değil)
        const resp = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, {
          pageNumber: 1,
          pageSize: 10,
          searchTerm: name,
          isActive: true,
          excludeVariantChildren: true,
        });
        const data = resp.data || {};
        const rows: any[] = data.items || data.Items || [];
        const product = matchRowByName(rows, (r) => r.name ?? r.Name ?? '', name) || rows[0];
        if (!product) {
          return {
            success: false,
            error: `Ürün stokta bulunamadı: "${name}". Stokta olmayan bir kalemse isManual=true ile ekle.`,
          };
        }

        const pId = String(product.id ?? product.Id ?? '');
        const pName = String(product.name ?? product.Name ?? name);
        const pUnit = String(product.unit ?? product.Unit ?? 'adet');
        const pVat = Number(product.vatRate ?? product.VatRate) || 0;
        const pSct = Number(product.sctRate ?? product.SctRate) || 0;
        const pVatStatus = String(product.vatStatus ?? product.VatStatus ?? '');
        const salePrice = Number(product.salePrice ?? product.SalePrice) || 0;
        const purchasePrice = Number(product.purchasePrice ?? product.PurchasePrice) || 0;
        // satış / irsaliye / satış siparişi / satış teklifi → satış fiyatı;
        // alış / alış siparişi / alış teklifi → alış fiyatı
        const basePrice =
          cfgRef.current.formType === 'purchase' ||
          cfgRef.current.formType === 'orderPurchase' ||
          cfgRef.current.formType === 'offerPurchase'
            ? purchasePrice
            : salePrice;
        const stocks: any[] = product.warehouseStocks || product.WarehouseStocks || [];
        const whId =
          defaultWhId || String(stocks[0]?.warehouseId ?? stocks[0]?.WarehouseId ?? '') || undefined;

        const newItem = recalculateLineItem({
          id: Date.now().toString(),
          itemId: pId,
          itemName: pName,
          quantity: qty,
          displayQuantity: formatQuantityForDisplay(qty),
          unit: input.unit || pUnit,
          enteredPrice: input.unitPrice !== undefined ? Number(input.unitPrice) || 0 : basePrice,
          priceType:
            input.priceType === 'VAT_EXCLUDED' || input.priceType === 'VAT_INCLUDED'
              ? input.priceType
              : pVatStatus === 'included'
                ? 'VAT_INCLUDED'
                : 'VAT_EXCLUDED',
          unitPrice: 0,
          unitPriceWithVat: 0,
          vatRate: input.vatRate !== undefined ? Number(input.vatRate) || 0 : pVat,
          sctRate: pSct,
          discountRate: Number(input.discountRate) || 0,
          discountAmount: Number(input.discountAmount) || 0,
          discountType: input.discountAmount !== undefined ? 'AMOUNT' : 'PERCENT',
          warehouseId: whId,
          lineTotal: 0,
          netTotal: 0,
          vatAmount: 0,
          sctAmount: 0,
          grossTotal: 0,
        });
        cfgRef.current.setItems((prev) => [...prev, newItem]);
        return {
          success: true,
          message: `Ürün eklendi: ${pName} x${qty} (${newItem.enteredPrice} ${newItem.priceType === 'VAT_INCLUDED' ? 'KDV dahil' : 'KDV hariç'}, KDV %${newItem.vatRate})`,
          matchedProduct: pName,
          line: cfgRef.current.getState().items.length + 1,
        };
      },

      updateItem: (target: InvoiceFormItemTarget, changes: InvoiceFormItemInput) => {
        const items = cfgRef.current.getState().items;
        const idx = findItemIndex(items, target);
        if (idx < 0) {
          return {
            success: false,
            error: 'Güncellenecek kalem bulunamadı — satır no (line) veya kalem adı (itemName) ver.',
            currentItems: summarizeItems(items),
          };
        }
        cfgRef.current.setItems((prev) =>
          prev.map((it, i) => (i === idx ? applyItemChanges(it, changes) : it))
        );
        return { success: true, message: `Kalem güncellendi: satır ${idx + 1} (${items[idx].itemName})` };
      },

      removeItem: (target: InvoiceFormItemTarget) => {
        const items = cfgRef.current.getState().items;
        const idx = findItemIndex(items, target);
        if (idx < 0) {
          return {
            success: false,
            error: 'Silinecek kalem bulunamadı — satır no (line) veya kalem adı (itemName) ver.',
            currentItems: summarizeItems(items),
          };
        }
        cfgRef.current.setItems((prev) => prev.filter((_, i) => i !== idx));
        return { success: true, message: `Kalem çıkarıldı: satır ${idx + 1} (${items[idx].itemName})` };
      },

      getSummary: () => {
        const s = cfgRef.current.getState();
        return {
          success: true,
          formType: cfgRef.current.formType,
          account: s.accountName,
          invoiceNumber: s.invoiceNumber,
          invoiceDate: s.invoiceDate,
          dueDate: s.dueDate,
          currency: s.currency,
          itemCount: s.items.length,
          items: summarizeItems(s.items),
          totals: { subtotal: s.subtotal, vatTotal: s.vatTotal, grandTotal: s.grandTotal },
        };
      },

      save: async () => {
        const s = cfgRef.current.getState();
        const ft = cfgRef.current.formType;
        const isWaybill = ft === 'waybill';
        const isOrder = ft === 'orderSales' || ft === 'orderPurchase';
        const isOffer = ft === 'offerSales' || ft === 'offerPurchase';
        const docLabel = isWaybill ? 'İrsaliye' : isOrder ? 'Sipariş' : isOffer ? 'Teklif' : 'Fatura';
        const setAccountHint = isWaybill
          ? 'setWaybillFormAccount'
          : isOrder
            ? 'setOrderFormAccount'
            : isOffer
              ? 'setOfferFormAccount'
              : 'setInvoiceFormAccount';
        const addItemHint = isWaybill
          ? 'addWaybillFormItem'
          : isOrder
            ? 'addOrderFormItem'
            : isOffer
              ? 'addOfferFormItem'
              : 'addInvoiceFormItem';
        if (!s.accountName) {
          return { success: false, error: `Önce cari seçilmeli (${setAccountHint}).` };
        }
        if (s.items.length === 0) {
          return {
            success: false,
            error: `${docLabel}de en az bir kalem olmalı (${addItemHint}).`,
          };
        }
        await cfgRef.current.save();
        // handleSaveInvoice hatayı state'e yazar (throw etmez) → re-render sonrası oku
        await new Promise((r) => setTimeout(r, 600));
        const after = cfgRef.current.getState();
        if (after.error) return { success: false, error: after.error };
        return {
          success: true,
          message: `${docLabel} kaydedildi: ${s.invoiceNumber || '(numara otomatik)'} — toplam ${s.grandTotal} ${s.currency}`,
        };
      },
    };

    return registerInvoiceFormApi(api);
    // API bir kez kaydedilir; state'e cfgRef üzerinden erişildiği için bağımlılık gerekmez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.enabled, config.formType]);
};
